import React, { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { MdContentCopy, MdCheck } from "react-icons/md";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { useTranslation } from "react-i18next";

/** Utils */
const timeZone = "Asia/Karachi";

function safeLower(v) {
  return (v ?? "").toString().toLowerCase();
}

function formatTimeHHMM(time) {
  // expects "HH:MM[:SS]" -> "h:mm AM/PM"
  if (!time) return "-";
  const [hh, mm] = time.split(":");
  let h = parseInt(hh, 10);
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mm} ${period}`;
}

function formatDate(dt) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(dt));
  } catch {
    return new Date(dt).toLocaleDateString();
  }
}

function moneyPKR(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return "Rs.0";
  return `Rs.${num.toLocaleString("en-PK")}`;
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[...Array(6)].map((_, i) => (
        <td key={i} className="py-4 px-2">
          <div className="h-4 w-32 bg-gray-200 rounded" />
        </td>
      ))}
    </tr>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1 ring-1 ring-slate-200">
      {children}
    </span>
  );
}

function OnlineCLasses() {
  const { t } = useTranslation();
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  // search / sort / pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("Newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [specificLink, setSpecificLink] = useState("");
  const [copied, setCopied] = useState(false);

  // expand rows
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setErr("");
        setLoading(true);
        const res = await axios.get(
          `${apiKey}/api/edolutions/admin/getAllReadNotificationsOnline`,
          { headers: { Authorization: `Bearer ${admin?.token}` } }
        );
        const incoming = res?.data?.data ?? [];
        setRawData(Array.isArray(incoming) ? incoming : []);
      } catch (e) {
        console.error(e);
        setErr(t("adminDash.onlineClasses.loadError"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [admin?.token]);

  // stable, memoized transform pipeline
  const transformed = useMemo(() => {
    // sort (work on a copy to avoid mutating state)
    const sorted = [...rawData].sort((a, b) => {
      const da = new Date(a?.created_at || 0).getTime();
      const db = new Date(b?.created_at || 0).getTime();
      return sortOption === "Oldest" ? da - db : db - da;
    });

    // filter
    const term = safeLower(searchTerm);
    const filtered = term
      ? sorted.filter((it) => {
          const teacherName = safeLower(it.teacher_name);
          const s = safeLower(it.student_name);
          const c = safeLower(it.class_name);
          const sub = safeLower(it.subjects);
          return (
            teacherName.includes(term) ||
            s.includes(term) ||
            c.includes(term) ||
            sub.includes(term)
          );
        })
      : sorted;

    return filtered;
  }, [rawData, searchTerm, sortOption]);

  // pagination
  const totalItems = transformed.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const pageSlice = useMemo(
    () => transformed.slice(startIndex, endIndex),
    [transformed, startIndex, endIndex]
  );

  useEffect(() => {
    // whenever sort/search/itemsPerPage change, reset to page 1
    setCurrentPage(1);
  }, [sortOption, searchTerm, itemsPerPage]);

  const handleToggleDetails = (index) =>
    setExpandedRow(expandedRow === index ? null : index);

  const openLinkModal = (link) => {
    setSpecificLink(link || "");
    setCopied(false);
    setShowLinkModal(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(specificLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore#EE5F2B
    }
  };

  // simple windowed page numbers (max 5)
  const windowedPages = useMemo(() => {
    const windowSize = 5;
    if (totalPages <= windowSize) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    return Array.from({ length: windowSize }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <>
      <div className="flex mx-auto items-center justify-center xl:p-5 sm:p-0">
        <div className="relative w-full border rounded-3xl shadow-sm p-5 bg-white">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-2">
            <p className="text-[22px] font-bold">{t("adminDash.onlineClasses.onlineSessions")}</p>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative border rounded-md flex items-center px-4 py-2 shadow-sm">
                <FiSearch className="h-5 w-5 absolute left-2 text-[#5963F5]" />
                <input
                  type="text"
                  className="outline-none pl-7 text-sm"
                  placeholder={t("adminDash.onlineClasses.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="relative">
                <select
                  className="border shadow-sm rounded-md cursor-pointer px-3 py-2 outline-none text-sm"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="Newest">{t("adminDash.onlineClasses.sortByNewest")}</option>
                  <option value="Oldest">{t("adminDash.onlineClasses.sortByOldest")}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error */}
          {err && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {err}
            </div>
          )}

          {/* Table */}
          <div className="mt-5 overflow-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm text-left text-gray-700">
              <thead className="bg-gray-50 text-[#6b7280]">
                <tr>
                  <th className="py-4 px-2 font-medium">{t("adminDash.onlineClasses.mentors")}</th>
                  <th className="py-4 px-2 font-medium">{t("adminDash.onlineTutoring.mentee")}</th>
                  <th className="py-4 px-2 font-medium">{t("adminDash.studentTution.profession")}</th>
                  <th className="py-4 px-2 font-medium">{t("adminDash.academicSubject.specializationLabel")}</th>
                  <th className="py-4 px-2 font-medium">{t("adminDash.onlineClasses.join")}</th>
                  <th className="py-4 px-2 font-medium">{t("adminDash.homeTeacher.details")}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading && (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                )}

                {!loading && totalItems === 0 && (
                  <tr>
                    <td colSpan="6" className="py-8 px-2 text-center text-gray-500">
                      {t("adminDash.onlineClasses.noEntriesFound")}
                    </td>
                  </tr>
                )}

                {!loading &&
                  pageSlice.map((item, idx) => {
                    const rowKey = `${item?.id || "row"}-${startIndex + idx}`;
                    const isExpanded = expandedRow === startIndex + idx;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="py-4 px-2">{item?.teacher_name || "-"}</td>
                          <td className="py-4 px-2">{item?.student_name || "-"}</td>
                          <td className="py-4 px-2">{item?.class_name || "-"}</td>
                          <td className="py-4 px-2">{item?.subjects || "-"}</td>
                          <td className="py-4 px-2">
                            <button
                              className="text-xs font-medium rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1 transition"
                              onClick={() => openLinkModal(item?.join_url)}
                              disabled={!item?.join_url}
                              title={item?.join_url ? t("adminDash.onlineClasses.openJoinLink") : t("adminDash.onlineClasses.noLink")}
                            >
                              {item?.join_url ? t("adminDash.onlineClasses.link") : t("adminDash.teacherMessage.na")}
                            </button>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              className="text-[#EE5F2B] hover:opacity-80"
                              onClick={() => handleToggleDetails(startIndex + idx)}
                              title={isExpanded ? t("adminDash.onlineClasses.hideDetails") : t("adminDash.onlineClasses.showDetails")}
                            >
                              {isExpanded ? (
                                <AiOutlineEyeInvisible className="text-lg" />
                              ) : (
                                <AiOutlineEye className="text-lg" />
                              )}
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan="6" className="bg-white">
                              <div className="m-2 rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className=" bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] ] text-white">
                                    <tr>
                                      <th className="py-3 px-2 text-left">{t("adminDash.studentTution.day")}</th>
                                      <th className="py-3 px-2 text-left">{t("adminDash.studentTution.timing")}</th>
                                      <th className="py-3 px-2 text-left">{t("adminDash.demoClass.durationLabel")}</th>
                                      <th className="py-3 px-2 text-left">{t("adminDash.studentTution.payment")}</th>
                                      <th className="py-3 px-2 text-left">{t("adminDash.onlineClasses.created")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="py-4 px-2 capitalize">
                                        {item?.day_name || "Monday"}
                                      </td>
                                      <td className="py-4 px-2">
                                        {item?.start_time && item?.end_time
                                          ? `${formatTimeHHMM(item.start_time)} – ${formatTimeHHMM(item.end_time)}`
                                          : item?.timeslot_id || "-"}
                                      </td>
                                      <td className="py-4 px-2">
                                        {`${item?.days ?? "-"} ${item?.duration ?? ""}`.trim()}
                                      </td>
                                      <td className="py-4 px-2">
                                        {moneyPKR(item?.payment)}
                                      </td>
                                      <td className="py-4 px-2">
                                        {formatDate(item?.created_at)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                {/* Meta row */}
                                <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 p-3 text-xs text-gray-500">
                                  {item?.join_url && <Badge>{t("adminDash.onlineClasses.hasJoinUrl")}</Badge>}
                                  {item?.timeslot_id && <Badge>{t("adminDash.onlineClasses.slot")}: {item.timeslot_id}</Badge>}
                                  {item?.created_at && (
                                    <Badge>ID: {item?.id ?? "—"}</Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap gap-4 justify-between items-center w-full text-sm mt-4">
            <div className="flex items-center gap-2 text-gray-600">
              <select
                className="text-sm border rounded-lg px-2 py-1 bg-white"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                }}
              >
                {[10, 20, 30, 40, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>{t("adminDash.common.showing")}</span>
              <span className="font-medium text-gray-800">
                {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
              </span>
              <span>{t("adminDash.common.of")} {totalItems} {t("adminDash.common.entries")}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="rounded-lg px-2 py-1 bg-gray-100 text-gray-800 disabled:opacity-40"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >
                &lt;
              </button>

              {windowedPages.map((p) => (
                <button
                  key={p}
                  className={`rounded-lg px-3 py-1 ${
                    p === currentPage
                      ? " bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]  text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}

              <span className="text-gray-500">{t("adminDash.common.of")} {totalPages} {t("adminDash.common.pages")}</span>

              <button
                className="rounded-lg px-2 py-1 bg-gray-100 text-gray-800 disabled:opacity-40"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                &gt;
              </button>
            </div>
          </div>

          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-white/70 backdrop-blur-sm rounded-3xl">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-[#EE5F2B] rounded-full animate-spin" />
                <p className="mt-3 text-sm font-semibold text-[#EE5F2B]">
                  {t("adminDash.common.loading")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Join Link Modal */}
      {showLinkModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <h2 className="text-lg font-semibold">{t("adminDash.onlineClasses.joinLink")}</h2>
                <button
                  className="text-2xl text-gray-600 hover:text-gray-800"
                  onClick={() => setShowLinkModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="px-5 py-5">
                {specificLink ? (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      {t("adminDash.onlineClasses.shareOrOpenLink")}
                    </p>
                    <div className="flex items-center gap-2">
                      <a
                        href={specificLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-blue-600 hover:underline"
                        title={specificLink}
                      >
                        {specificLink}
                      </a>
                      <button
                        onClick={copyToClipboard}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200"
                      >
                        {copied ? <MdCheck /> : <MdContentCopy />}
                        {copied ? t("adminDash.onlineClasses.copied") : t("adminDash.onlineClasses.copy")}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600">{t("adminDash.onlineClasses.noLinkAvailable")}</p>
                )}
              </div>
              <div className="border-t px-5 py-3 flex justify-end">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm"
                  onClick={() => setShowLinkModal(false)}
                >
                  {t("adminDash.common.close")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default OnlineCLasses;
